'use client';

// Core Imports
import React, { useEffect, useState, useRef } from 'react';

// Component Imports
import ProductCard from '@/components/product/productCard';

// Function Imports
import { getRelatedProducts } from '@/lib/functions/getRelatedProducts';
import { isPriceValid } from '@/lib/functions/isPriceValid';
import { defaultStoreNumber } from '@/lib/constants/store';

// Style Imports
import styles from './relatedProducts.module.scss';

// Type Imports
import type { NormalizedProduct } from '@/lib/types/products/normalizedProduct';

interface RelatedProductsProps {
  slug: string;
}

/*
 * RelatedProducts
 *
 * Fetches and displays a list of related products based on a given slug.
 * API returns NormalizedProduct[] (via searchProductMapper), including dimensions
 * used to disable add-to-cart when a product is not shippable.
 */
export const RelatedProducts: React.FC<RelatedProductsProps> = ({ slug }) => {
  const [relatedProducts, setRelatedProducts] = useState<NormalizedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (slug && !hasFetched.current) {
        hasFetched.current = true;
        const products = await getRelatedProducts(slug, defaultStoreNumber);
        setRelatedProducts(Array.isArray(products) ? products : []);
        setIsLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [slug]);

  const displayProducts = isLoading
    ? []
    : relatedProducts.filter((product) => product.code != null && isPriceValid(product.pricing?.unitPrice));

  if (!isLoading && displayProducts.length === 0) {
    return null;
  }

  return (
    <div className={styles.relatedProductsWrapper}>
      <h3 className={styles.header}>Related Products</h3>
      <div className={styles.productGrid}>
        {(isLoading ? new Array(5).fill(null) : displayProducts).map((product: NormalizedProduct, index) => {
          if (!isLoading && (!product || product.code == null)) return null;
          return (
            <ProductCard
              key={`p-${index}`}
              product={product}
              loading={isLoading}
            />
          );
        })}
      </div>
    </div>
  );
};
